import { Input } from 'components/UIComponents/index';
import { shortFieldStyles } from './styledComponents';
import { useFormikContext } from 'formik';

interface DependentSelectsBlockProps {
  impressionsValue: number | '';
  impressionsError?: string;
  grossRateValue: string | '';
  netRateValue: string | '';
  grossRateError?: string;
  netRateError?: string;
  cpmRateValue: string | '';
  cpmRateError?: string;
  setFieldValue: (name: string, value: any) => void;
  isNetRateDisabled?: boolean;
  isPaid?: boolean;
  isImpressionsDisabled?: boolean;
}

const CALCULATE_VALUE = 0.85;
const MULTIPLIER = 100;
const CPM_MULTIPLIER = 1000;

const PodcastRateFields = ({
  impressionsValue,
  impressionsError,
  grossRateValue,
  netRateValue,
  grossRateError,
  netRateError,
  cpmRateValue,
  cpmRateError,
  setFieldValue,
  isNetRateDisabled = false,
  isPaid = false,
  isImpressionsDisabled = false,
}: DependentSelectsBlockProps) => {
  const { validateForm, validateField, setFieldTouched } = useFormikContext();

  const onChangeNetValue = (
    grossValue: string,
    setFieldValue: (field: string, value: any) => void,
  ) => {
    const netValue =
      Math.round(Number(grossValue) * CALCULATE_VALUE * MULTIPLIER) /
        MULTIPLIER;
    setFieldValue('netRate', netValue.toString());
    setFieldTouched('netRate', true);
  };

  const onChangeGrossValue = (
    netValue: string,
    setFieldValue: (field: string, value: any) => void,
  ) => {
    const grossValue =
      Math.round((Number(netValue) / CALCULATE_VALUE) * MULTIPLIER) /
        MULTIPLIER;
    setFieldValue('grossRate', grossValue.toString());
    setFieldTouched('grossRate', true);
    return grossValue.toString();
  };

  const onChangeCpmValue = (
    grossValue: string,
    impressionsValue: number | '',
    setFieldValue: (field: string, value: any) => void,
  ) => {
    const cpmValue = Math.round(
      (Number(grossValue) / Number(impressionsValue)) * CPM_MULTIPLIER * MULTIPLIER
    ) / MULTIPLIER;
    setFieldValue('cpmRate', cpmValue.toString());
    setFieldTouched('cpmRate', true);
  };

  const onNetRateBlur = async (netRateValue: string | '') => {
    let grossValue = grossRateValue || '';
    if (netRateValue && Number(netRateValue) > 0 && !netRateError) {
      grossValue = onChangeGrossValue(netRateValue, setFieldValue);
    }

    if (netRateValue && Number(netRateValue) > 0 && impressionsValue && !impressionsError && !netRateError) {
      onChangeCpmValue(grossValue, impressionsValue, setFieldValue);
    }

    if (netRateValue
      && Number(netRateValue) > 0
      && cpmRateValue
      && !cpmRateError
      && !netRateError
      && !impressionsValue
    ) {
      const impressionsValue = Math.round(
        (Number(grossValue) / Number(cpmRateValue)) * CPM_MULTIPLIER
      );
      setFieldValue('impressions', impressionsValue);
      setFieldTouched('impressions', true);
      onChangeCpmValue(grossValue, impressionsValue, setFieldValue);
    }

    if (!netRateValue && impressionsValue && cpmRateValue && !impressionsError) {
      const grossValue = Math.round(
        (Number(cpmRateValue) * impressionsValue) / CPM_MULTIPLIER * MULTIPLIER
      ) / MULTIPLIER;
      setFieldValue('grossRate', grossValue.toString());
      setFieldTouched('grossRate', true);
      onChangeNetValue(grossValue.toString(), setFieldValue);
      onChangeCpmValue(grossValue.toString(), impressionsValue, setFieldValue);
    }

    await validateForm();
    validateField('impressions');
    validateField('cpmRate');
    validateField('grossRate');
    validateField('netRate');
  };

  const onGrossRateBlur = async (grossRateValue: string | '') => {
    if (grossRateValue && Number(grossRateValue) > 0 && !grossRateError) {
      onChangeNetValue(grossRateValue, setFieldValue);
    }

    if (grossRateValue && Number(grossRateValue) > 0 && impressionsValue && !impressionsError && !grossRateError) {
      onChangeCpmValue(grossRateValue, impressionsValue, setFieldValue);
    }

    if (grossRateValue
      && Number(grossRateValue) > 0
      && cpmRateValue
      && !cpmRateError
      && !grossRateError
      && !impressionsValue
    ) {
      const impressionsValue = Math.round(
        (Number(grossRateValue) / Number(cpmRateValue)) * CPM_MULTIPLIER
      );
      setFieldValue('impressions', impressionsValue);
      setFieldTouched('impressions', true);
      onChangeCpmValue(grossRateValue, impressionsValue, setFieldValue);
    }

    if (!grossRateValue && impressionsValue && cpmRateValue && !impressionsError) {
      const grossValue = Math.round(
        (Number(cpmRateValue) * impressionsValue) / CPM_MULTIPLIER * MULTIPLIER
      ) / MULTIPLIER;
      setFieldValue('grossRate', grossValue.toString());
      setFieldTouched('grossRate', true);
      onChangeNetValue(grossValue.toString(), setFieldValue);
      onChangeCpmValue(grossValue.toString(), impressionsValue, setFieldValue);
    }

    await validateForm();
    validateField('impressions');
    validateField('cpmRate');
    validateField('grossRate');
    validateField('netRate');
  };

  const onCpmBlur = async (cpmValue: string | '') => {
    if (cpmValue && Number(cpmValue) > 0 && impressionsValue && !cpmRateError && !impressionsError) {
      const grossValue = Math.round(
        (Number(cpmValue) * impressionsValue) / CPM_MULTIPLIER * MULTIPLIER
      ) / MULTIPLIER;
      setFieldValue('grossRate', grossValue.toString());
      setFieldTouched('grossRate', true);
      onChangeNetValue(grossValue.toString(), setFieldValue);
    }

    if (cpmValue && Number(cpmValue) > 0 && grossRateValue && !grossRateError && !cpmRateError) {
      const impressionsValue = Math.round(
        (Number(grossRateValue) / Number(cpmValue)) * CPM_MULTIPLIER
      );
      setFieldValue('impressions', impressionsValue);
      setFieldTouched('impressions', true);
      onChangeCpmValue(grossRateValue, impressionsValue, setFieldValue);
    }

    if (!cpmValue && impressionsValue && grossRateValue && !impressionsError && !grossRateError) {
      onChangeCpmValue(grossRateValue, impressionsValue, setFieldValue);
    }

    await validateForm();
    validateField('impressions');
    validateField('cpmRate');
    validateField('grossRate');
    validateField('netRate');
  };

  const onImpressionsBlur = async (impressionsValue: number | '') => {
    let grossValue = Number(grossRateValue) || 0;
    if (
      impressionsValue
        && impressionsValue > 0
        && cpmRateValue
        && !cpmRateError
        && !impressionsError
        && !grossRateValue
    ) {
      grossValue = Math.round(
        (Number(cpmRateValue) * impressionsValue) / CPM_MULTIPLIER * MULTIPLIER
      ) / MULTIPLIER;
      setFieldValue('grossRate', grossValue.toString());
      setFieldTouched('grossRate', true);
      onChangeNetValue(grossValue.toString(), setFieldValue);
      onChangeCpmValue(grossValue.toString(), impressionsValue, setFieldValue);
    }

    if (
      impressionsValue
        && impressionsValue > 0
        && grossRateValue
        && !grossRateError
        && !impressionsError
    ) {
      const cpmValue = Math.round(
        (grossValue / impressionsValue) * CPM_MULTIPLIER * MULTIPLIER
      ) / MULTIPLIER;
      setFieldValue('cpmRate', cpmValue.toString());
      setFieldTouched('cpmRate', true);
    }

    if (
      !impressionsValue
      && grossRateValue
      && cpmRateValue
      && !grossRateError
    ) {
      const impressionsValue = Math.round(
        (Number(grossRateValue) / Number(cpmRateValue)) * CPM_MULTIPLIER
      );

      setFieldValue('impressions', impressionsValue);
      setFieldTouched('impressions', true);
      onChangeCpmValue(grossRateValue, impressionsValue, setFieldValue);
    }

    await validateForm();
    validateField('impressions');
    validateField('cpmRate');
    validateField('grossRate');
    validateField('netRate');
  };

  return (
    <>
      <Input
        name="impressions"
        label="Impressions"
        required
        styles={shortFieldStyles}
        disabled={isImpressionsDisabled}
        onBlur={() => onImpressionsBlur(impressionsValue)}
      />
      <Input
        name="grossRate"
        label="Gross Rate"
        required
        styles={shortFieldStyles}
        disabled={isPaid}
        onBlur={() => onGrossRateBlur(grossRateValue)}
      />
      <Input
        name="netRate"
        label="Net Rate"
        required
        onBlur={() => onNetRateBlur(netRateValue)}
        disabled={isPaid ? isPaid : isNetRateDisabled}
        styles={shortFieldStyles}
      />
      <Input
        name="cpmRate"
        label="CPM Rate"
        required
        onBlur={() => onCpmBlur(cpmRateValue)}
        styles={shortFieldStyles}
        disabled={isPaid}
      />
    </>
  );
};

export default PodcastRateFields;
